import {
  faBolt,
  faCircleInfo,
  faEllipsis,
  faEye,
  faEyeSlash,
  faPencil,
  faShield,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ActionIcon,
  AspectRatio,
  Badge,
  Grid,
  Group,
  Image,
  Menu,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { useSessionInfo } from '../../contexts/SessionContext'
import { Device } from '../../models/Device'
import { Location } from '../../models/Location'
import { isUserAdmin } from '../../utils/authUtils'
import { COLORS } from '../../utils/constants'
import { convertServerDateToLocal } from '../../utils/dateUtils'
import { formatApprovalType, formatNumber } from '../../utils/displayUtils'

interface DeviceInfoCardProps {
  device: Device
  location: Location
  onManageDevice: () => void
  onToggleVisibility: () => void
  onDeleteDevice: () => void
}

export function DeviceInfoCard({
  device,
  location,
  onManageDevice,
  onToggleVisibility,
  onDeleteDevice,
}: DeviceInfoCardProps) {
  const { session } = useSessionInfo()
  const isAdmin = isUserAdmin(session?.user?.id)

  return (
    <Paper shadow='md' radius='lg' p='xl' withBorder>
      <Grid gutter='xl' align='center'>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <AspectRatio ratio={16 / 9}>
            <Image
              src={device.imageUrl}
              alt={device.name}
              radius='md'
              fit='cover'
            />
          </AspectRatio>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Stack gap='md'>
            <Stack gap='md'>
              <Group gap='md' align='center'>
                <Title order={2} mb={4}>
                  {device.name}
                </Title>
                <Menu shadow='md' width={200}>
                  <Menu.Target>
                    <ActionIcon
                      bg={COLORS.LightGray}
                      style={{
                        borderRadius: 12,
                        padding: 10,
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faEllipsis}
                        size='lg'
                        color='black'
                      />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<FontAwesomeIcon icon={faPencil} />}
                      onClick={onManageDevice}
                    >
                      Edit Device
                    </Menu.Item>

                    {device.isVisible ? (
                      <Menu.Item
                        leftSection={<FontAwesomeIcon icon={faEyeSlash} />}
                        onClick={onToggleVisibility}
                        rightSection={
                          <Tooltip label='Disallow others to book ads on this device'>
                            <span>
                              <FontAwesomeIcon icon={faCircleInfo} />
                            </span>
                          </Tooltip>
                        }
                      >
                        Hide Device
                      </Menu.Item>
                    ) : (
                      <Menu.Item
                        leftSection={<FontAwesomeIcon icon={faEye} />}
                        onClick={onToggleVisibility}
                        rightSection={
                          <Tooltip label='Allow others to book ads on this device'>
                            <span>
                              <FontAwesomeIcon icon={faCircleInfo} />
                            </span>
                          </Tooltip>
                        }
                      >
                        Show Device
                      </Menu.Item>
                    )}

                    <Menu.Item
                      leftSection={<FontAwesomeIcon icon={faTrash} />}
                      color='red'
                      onClick={onDeleteDevice}
                    >
                      Delete Device
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
              <Text size='sm' c='dimmed'>
                {device.screenSize}" TV
              </Text>
            </Stack>

            <Group gap='xl'>
              <div>
                <Text size='sm' c='dimmed' mb={4}>
                  Monthly Price
                </Text>
                <Text fw={600} size='lg'>
                  ${formatNumber(device.monthlyPrice)}
                </Text>
              </div>
              <div>
                <Text size='sm' c='dimmed' mb={4}>
                  Device Code
                </Text>
                <Text fw={600} size='lg'>
                  {device.deviceCode}
                </Text>
              </div>
            </Group>

            <Group gap='xl'>
              <div>
                <Text size='sm' c='dimmed' mb={4}>
                  Location
                </Text>
                <Text fw={600}>{location.name}</Text>
              </div>
              <div>
                <Text size='sm' c='dimmed' mb={4}>
                  Status
                </Text>
                <Badge
                  color={device.isVisible ? 'green' : 'red'}
                  leftSection={
                    <FontAwesomeIcon
                      icon={device.isVisible ? faEye : faEyeSlash}
                    />
                  }
                >
                  {device.isVisible ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div>
                <Text size='sm' c='dimmed' mb={4}>
                  Approval Type
                </Text>
                <Badge
                  color={
                    device.approvalType === 'Auto'
                      ? COLORS.HiddenGemzGold
                      : COLORS.HiddenGemzRed
                  }
                  variant='filled'
                  leftSection={
                    <FontAwesomeIcon
                      icon={device.approvalType === 'Auto' ? faBolt : faShield}
                      color='white'
                    />
                  }
                >
                  {formatApprovalType(device.approvalType)}
                </Badge>
              </div>
              {isAdmin && device.lastRequestDate && (
                <div>
                  <Text size='sm' c='dimmed' mb={4}>
                    Last Active
                  </Text>
                  <Text fw={600} size='lg'>
                    {convertServerDateToLocal(
                      device.lastRequestDate,
                    )?.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Text>
                </div>
              )}
            </Group>
          </Stack>
        </Grid.Col>
      </Grid>
    </Paper>
  )
}
